import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import GeneratedDocuments from "components/layers/GeneratedDocuments";
import { useState } from "react";
import NumberFormat from "react-number-format";
import { useSelector } from "react-redux";
import * as generatedDocumentsService from "services/generatedDocumentsService";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const POLICY_MEMBER_BG = "#C8E6C9";
const SELECTED_BG = "#E8F5E9";

const rowBackground = ({ isSelected, isInActivePolicy, isPolicyActive }) => {
  if (isInActivePolicy) return POLICY_MEMBER_BG;
  if (isSelected && !isPolicyActive) return SELECTED_BG;
  return "transparent";
};

const LayerRow = ({
  layer,
  index,
  isSelected,
  onSelect,
  submissionId,
  isInActivePolicy,
  isPolicyActive,
}) => {
  const [expanded, setExpanded] = useState(false);

  const {
    data: documentsResponse,
  } = generatedDocumentsService.useListGeneratedDocumentsQuery(
    {
      submissionId,
      layerId: layer.id,
    },
    {
      skip: !submissionId || !layer.id,
    }
  );

  const documents = documentsResponse?.documents?.[layer.id] ?? [];
  const hasDocuments = documents && documents.length > 0;

  return (
    <>
      <TableRow
        onClick={isPolicyActive ? undefined : () => onSelect(index)}
        sx={{
          cursor: isPolicyActive ? "default" : "pointer",
          backgroundColor: rowBackground({
            isSelected,
            isInActivePolicy,
            isPolicyActive,
          }),
          "&:hover": {
            backgroundColor: isInActivePolicy
              ? POLICY_MEMBER_BG
              : isPolicyActive
              ? "transparent"
              : isSelected
              ? SELECTED_BG
              : "#F5F5F5",
          },
        }}
      >
        <TableCell sx={{ width: "40px", padding: "4px 8px" }}>
          {hasDocuments && (
            <IconButton
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <StatusBadgeV2
            status={layer.status}
            onChange={() => {}}
            variant={"compact"}
          />
        </TableCell>
        <TableCell align={"left"}>{layer.carrier ?? ""}</TableCell>
        <TableCell align={"left"}>{layer.coverage ?? ""}</TableCell>
        <TableCell align={"left"}>{layer.retroactiveDate ?? ""}</TableCell>
        <TableCell align={"right"}>
          <NumberFormat
            value={layer.lineSize}
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            className={"monospace"}
          />
        </TableCell>
        <TableCell align={"right"}>
          <NumberFormat
            value={layer.shareOfPremium}
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            className={"monospace"}
          />
        </TableCell>
        <TableCell align={"right"}>
          <NumberFormat
            value={layer.limit}
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            className={"monospace"}
          />
        </TableCell>
        <TableCell align={"right"}>
          <NumberFormat
            value={layer.attachment}
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            className={"monospace"}
          />
        </TableCell>
        <TableCell align={"right"}>
          <NumberFormat
            value={layer.grossPremium}
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            className={"monospace"}
          />
        </TableCell>
        <TableCell align={"right"}>
          <NumberFormat
            value={layer.grossPPM}
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            className={"monospace"}
          />
        </TableCell>
      </TableRow>
      {hasDocuments && (
        <TableRow>
          <TableCell
            colSpan={11}
            sx={{
              paddingTop: 0,
              paddingBottom: expanded ? 2 : 0,
              borderBottom: expanded
                ? "1px solid rgba(224, 224, 224, 1)"
                : "none",
            }}
          >
            <Collapse in={expanded} timeout={"auto"} unmountOnExit>
              <GeneratedDocuments
                documents={documents}
                submissionId={submissionId}
                layerId={layer.id}
                layer={layer}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const LayersWithDocuments = ({
  layers,
  selectedIndex,
  onSelectLayer,
  title = "Layers",
  options,
}) => {
  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);
  const activePolicy = useSelector(pricingSelectors.getActivePolicy);
  const activePolicyLayerIds = new Set(activePolicy?.layers ?? []);

  return (
    <Component title={title} options={options}>
      <Box>
        <TableContainer component={Paper} variant={"outlined"}>
          <Table size={"small"} className={"condensed-table"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "40px" }} />
                <TableCell>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Status"}
                  </Typography>
                </TableCell>
                <TableCell align={"left"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Carrier"}
                  </Typography>
                </TableCell>
                <TableCell align={"left"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Coverage"}
                  </Typography>
                </TableCell>
                <TableCell align={"left"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Retro Date"}
                  </Typography>
                </TableCell>
                <TableCell align={"right"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Our Limit"}
                  </Typography>
                </TableCell>
                <TableCell align={"right"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Our Premium"}
                  </Typography>
                </TableCell>
                <TableCell align={"right"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Part of"}
                  </Typography>
                </TableCell>
                <TableCell align={"right"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Attachment"}
                  </Typography>
                </TableCell>
                <TableCell align={"right"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"100% Premium"}
                  </Typography>
                </TableCell>
                <TableCell align={"right"}>
                  <Typography variant={"subtitle2"} fontWeight={"bold"}>
                    {"Gross PPM"}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {layers?.map((layer, index) => (
                <LayerRow
                  key={layer.id || index}
                  layer={layer}
                  index={index}
                  isSelected={index === selectedIndex}
                  onSelect={onSelectLayer}
                  submissionId={submissionId}
                  isInActivePolicy={activePolicyLayerIds.has(layer.id)}
                  isPolicyActive={activePolicy !== null}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Component>
  );
};

export default LayersWithDocuments;
